import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authFetch } from '../../utils/authFetch';

const EstatesList = () => {
    const [estates, setEstates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [formData, setFormData] = useState({
        deceased_name: '', deceased_id_number: '', date_of_death: '',
        place_of_death: '', last_known_address: '', estate_reference: '',
        estate_type: 'TESTATE', currency: 'USD', client: '', assigned_to: '',
        notes: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => { fetchEstates(); }, [statusFilter, typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchEstates = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.append('status', statusFilter);
            if (typeFilter) params.append('estate_type', typeFilter);
            if (searchTerm) params.append('search', searchTerm);
            const response = await authFetch(`/api/estates/estates/?${params}`);
            if (response.ok) {
                const data = await response.json();
                setEstates(Array.isArray(data) ? data : data.results || []);
            }
        } catch (err) {
            console.error('Failed to fetch estates:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchEstates();
    };

    const handleCreateEstate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const response = await authFetch('/api/estates/estates/', {
                method: 'POST',
                body: JSON.stringify(formData)
            });
            if (response.ok) {
                const newEstate = await response.json();
                setShowCreateModal(false);
                navigate(`/estates/${newEstate.id}`);
            }
        } catch (err) {
            console.error('Failed to create estate:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
    };

    const statusColors = {
        INTAKE: 'bg-blue-100 text-blue-700',
        PROBATE: 'bg-yellow-100 text-yellow-700',
        ADMINISTRATION: 'bg-purple-100 text-purple-700',
        DISTRIBUTION: 'bg-green-100 text-green-700',
        CLOSED: 'bg-gray-100 text-gray-600',
        DISPUTED: 'bg-red-100 text-red-700',
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Estates</h1>
                    <p className="text-gray-600 mt-1">Manage all deceased estates and probate matters</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                    New Estate
                </button>
            </div>

            {/* Search & Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <input
                            type="text"
                            placeholder="Search by name, reference, or case number..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                        <option value="">All Statuses</option>
                        <option value="INTAKE">Intake</option>
                        <option value="PROBATE">Probate</option>
                        <option value="ADMINISTRATION">Administration</option>
                        <option value="DISTRIBUTION">Distribution</option>
                        <option value="CLOSED">Closed</option>
                        <option value="DISPUTED">Disputed</option>
                    </select>
                    <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                        <option value="">All Types</option>
                        <option value="TESTATE">Testate (With Will)</option>
                        <option value="INTESTATE">Intestate (Without Will)</option>
                        <option value="PARTIAL_INTESTATE">Partially Intestate</option>
                    </select>
                    <button type="submit" className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                        Search
                    </button>
                </form>
            </div>

            {/* Estates Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="p-8">
                        {[1, 2, 3, 4, 5].map(i => (
                            <div key={i} className="flex items-center py-4 border-b border-gray-100 last:border-b-0">
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 w-48 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-3 w-32 bg-gray-100 rounded animate-pulse"></div>
                                </div>
                                <div className="h-6 w-20 bg-gray-100 rounded-full animate-pulse"></div>
                            </div>
                        ))}
                    </div>
                ) : estates.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="mx-auto w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">No Estates Found</h3>
                        <p className="text-gray-500 mb-4">Get started by creating your first estate.</p>
                        <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                            New Estate
                        </button>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Deceased</th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Net Value</th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {estates.map((estate) => (
                                <tr key={estate.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <Link to={`/estates/${estate.id}`} className="font-medium text-gray-900 hover:text-indigo-600">{estate.deceased_name}</Link>
                                        <p className="text-sm text-gray-500">{estate.date_of_death}</p>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-700">{estate.estate_reference}</td>
                                    <td className="px-6 py-4 text-sm text-gray-700">{estate.estate_type_display}</td>
                                    <td className="px-6 py-4">
                                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColors[estate.status] || 'bg-gray-100 text-gray-600'}`}>
                                            {estate.status_display}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right font-medium text-gray-900">{formatCurrency(estate.net_estate_value, estate.currency)}</td>
                                    <td className="px-6 py-4 text-sm text-gray-700">{estate.assigned_to_name}</td>
                                    <td className="px-6 py-4 text-right">
                                        <Link to={`/estates/${estate.id}`} className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
                                            View →
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Create Estate Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setShowCreateModal(false)}></div>
                        <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full p-8 z-10 max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-900">Create New Estate</h2>
                                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <form onSubmit={handleCreateEstate} className="space-y-6">
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Deceased Information</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                                            <input type="text" required value={formData.deceased_name} onChange={e => setFormData({...formData, deceased_name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Full name of deceased" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">ID / Passport Number</label>
                                            <input type="text" value={formData.deceased_id_number} onChange={e => setFormData({...formData, deceased_id_number: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Death *</label>
                                            <input type="date" required value={formData.date_of_death} onChange={e => setFormData({...formData, date_of_death: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Place of Death</label>
                                            <input type="text" value={formData.place_of_death} onChange={e => setFormData({...formData, place_of_death: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Estate Details</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Estate Reference *</label>
                                            <input type="text" required value={formData.estate_reference} onChange={e => setFormData({...formData, estate_reference: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="e.g. EST-2026-001" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Estate Type *</label>
                                            <select value={formData.estate_type} onChange={e => setFormData({...formData, estate_type: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                                                <option value="TESTATE">Testate (With Will)</option>
                                                <option value="INTESTATE">Intestate (Without Will)</option>
                                                <option value="PARTIAL_INTESTATE">Partially Intestate</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                                            <select value={formData.currency} onChange={e => setFormData({...formData, currency: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                                                <option value="USD">USD - US Dollar</option>
                                                <option value="ZWL">ZWL - Zimbabwe Dollar</option>
                                                <option value="ZAR">ZAR - South African Rand</option>
                                                <option value="GBP">GBP - British Pound</option>
                                                <option value="EUR">EUR - Euro</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                    <textarea rows={3} value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Additional notes about the estate..." />
                                </div>

                                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                                    <button type="button" onClick={() => setShowCreateModal(false)} className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium">Cancel</button>
                                    <button type="submit" disabled={submitting} className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50">
                                        {submitting ? 'Creating...' : 'Create Estate'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EstatesList;
